import React, { useState, useEffect, useRef } from 'react';
import { Toast } from 'primereact/toast';
import AssignRoleSearch from './role-assignment-search';
import AssignRoleTable from './role-assignment-table';
import AssignRoleDialog from './role-assignment-dialog';
import RoleService from '../../services/user-role/role-service';
import { UserRolesSummaryDTO } from '../../models/user-role/role-model';
import AppLoading from '../app-loading/app-loading';
import './role-assignment.css';

const AssignRoleMain = () => {
    const [userRoles, setUserRoles] = useState<UserRolesSummaryDTO[]>([]);
    const [showAddDialog, setShowAddDialog] = useState(false);
    const toast = useRef<Toast>(null);

    const [filterParams, setFilterParams] = useState<{
        userId: number | null,
        roleId: number | null,
    }>({ userId: null, roleId: null });

    const [tableKey, setTableKey] = useState(0);
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);

    // FIXED: Initialize as null for NO default sort highlighting
    const [sortField, setSortField] = useState<string | null>(null);
    //const [sortOrder, setSortOrder] = useState<number | null>(null);
    const [sortOrder, setSortOrder] = useState<-1 | 0 | 1 | null>(null);

    const [loading, setLoading] = useState(false);

    // Load initial data with NO sort state
    useEffect(() => {
        loadUserRoles();
    }, []);

    const loadUserRoles = async (
        pageNumber: number = 1,
        pageSize: number = 10,
        sortByParam: string | null = null,
        sortOrderParam: string | null = null,
        searchRoleId: number = 0,
        searchUserId: number = 0
    ) => {
        try {
            setLoading(true);

            const firstRecord = (pageNumber - 1) * pageSize;
            setFirst(firstRecord);
            setRows(pageSize);

            // FIXED: Only set sort state if explicitly provided
            if (sortByParam) setSortField(sortByParam);
            if (sortOrderParam) {
                setSortOrder(sortOrderParam === "asc" ? 1 : -1);
            }

            // Backend defaults (service handles this)
            const finalSortBy = sortByParam || "modifiedDate";
            const finalSortOrder = sortOrderParam || "desc";

            console.log(`Loading: page=${pageNumber}, size=${pageSize}, sort=${finalSortBy} ${finalSortOrder}, roleId=${searchRoleId}, userId=${searchUserId}`);

            const data = await RoleService.getAllUserRoles(
                pageNumber, pageSize, finalSortBy, finalSortOrder, searchRoleId, searchUserId
            );
            console.log("API Response:", data);

            if (data && data.isSuccess) {
                setUserRoles(Array.isArray(data.data) ? data.data : []);
                setTotalRecords(data.totalRecords || 0);
            } else {
                console.error("API Error:", data);
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: data?.message || 'Failed to load user roles',
                    life: 3000
                });
                setUserRoles([]);
                setTotalRecords(0);
            }
        } catch (error: any) {
            console.error("Error loading user roles", error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message || 'Failed to load user roles',
                life: 3000
            });
            setUserRoles([]);
            setTotalRecords(0);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (selectedUser: number | null, selectedRole: number | null) => {
        const userId = selectedUser || 0;
        const roleId = selectedRole || 0;
        setFilterParams({
            userId: selectedUser,
            roleId: selectedRole,

        });
        // Reset to page 1, preserve current sort
        loadUserRoles(1, rows, sortField || null,
            sortOrder ? (sortOrder === 1 ? "asc" : "desc") : null,
            roleId, userId);
    };

    const handleReset = () => {
        setFilterParams({ userId: null, roleId: null });
        setTableKey(prev => prev + 1);
        // FIXED: Reset to NO sort highlighting
        setSortField(null);
        setSortOrder(null);
        setFirst(0);
        loadUserRoles(1, 10, null, null, 0, 0);
    };

    const onPage = (event: { first: number, rows: number }) => {
        const pageNumber = Math.floor(event.first / event.rows) + 1;
        loadUserRoles(pageNumber, event.rows, sortField || null,
            sortOrder ? (sortOrder === 1 ? "asc" : "desc") : null,
            filterParams.roleId || 0, filterParams.userId || 0);
    };

    const onSort = (event: { sortField: string, sortOrder: number }) => {
        loadUserRoles(1, rows, event.sortField,
            event.sortOrder === 1 ? "asc" : "desc",
            filterParams.roleId || 0, filterParams.userId || 0);
    };

    const refreshData = () => {
        const pageNumber = Math.floor(first / rows) + 1;
        loadUserRoles(pageNumber, rows, sortField || null,
            sortOrder ? (sortOrder === 1 ? "asc" : "desc") : null,
            filterParams.roleId || 0, filterParams.userId || 0);
    };

    return (
        <div className="flex flex-col min-h-screen font-sans bg-gray-50 pt-2 px-3 sm:px-4 md:px-6 pb-6">
            {loading && <AppLoading />}
            <Toast ref={toast} />
            <main className="flex-1 max-w-7xl mx-auto w-full animate-fade-in">

                {/* Modern Page Header */}
                <div className="role-assignment-header">
                    <div className="assignment-header-icon-wrapper">
                        <i className="pi pi-user-edit text-4xl"></i>
                    </div>
                    <div>
                        <h1 className="assignment-header-title">
                            Role Assignment
                        </h1>
                        <p className="assignment-header-subtitle">
                            Assign and manage user roles with precision and control.
                        </p>
                    </div>
                </div>

                <AssignRoleSearch
                    onSearch={handleSearch}
                    onAddClick={() => setShowAddDialog(true)}
                    onReset={handleReset}
                />

                <AssignRoleTable
                    key={tableKey}
                    data={userRoles}
                    totalRecords={totalRecords}
                    first={first}
                    rows={rows}
                    onPage={onPage}
                    // FIXED: Pass undefined when no sort (no green highlight)
                    sortField={sortField || undefined}
                    sortOrder={sortOrder || undefined}
                    onSort={onSort}
                    onRefresh={refreshData}
                />

                <AssignRoleDialog
                    visible={showAddDialog}
                    onHide={() => setShowAddDialog(false)}
                    onSaveSuccess={refreshData}
                    editMode={false}
                    toast={toast}
                />
            </main>
        </div>
    );
};

export default AssignRoleMain;
